import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Heart, Calendar, MapPin, Megaphone, CheckCircle, Phone, Star, Edit, Trash2, AlertCircle, Loader2, Users, Eye, MessageSquare, Lock, ChevronDown, ChevronUp } from 'lucide-react';
import dayjs from 'dayjs';
import { api, apiErrorMessage } from '../apiClient';
import { useAuth } from '../AuthContext';
import { toast } from 'sonner';

function Stars({ value, size = 14 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${i <= Math.round(value) ? 'text-brand-magenta fill-[#E91E63]' : 'text-gray-300'}`} style={{ width: size, height: size }} />
      ))}
    </div>
  );
}

function FAQAccordion({ faq, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-gray-100 py-3" data-testid={`event-faq-${faq.id}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-2 text-left">
        <span className="text-sm font-extrabold text-gray-900 flex-1">{faq.question}</span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {open && <p className="mt-2 text-sm text-gray-500 leading-relaxed" data-testid={`event-faq-answer-${faq.id}`}>{faq.answer}</p>}
    </div>
  );
}

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthed } = useAuth();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wishlisted, setWishlisted] = useState(false);
  const [wishBusy, setWishBusy] = useState(false);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [reviews, setReviews] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/events/${id}`);
      setEvent(r.data);
      const rv = await api.get(`/events/${id}/reviews`);
      setReviews(rv.data);
      if (isAuthed) {
        try {
          const [idsRes, elig] = await Promise.all([
            api.get('/wishlist/ids'),
            api.get(`/events/${id}/reviews/eligibility`),
          ]);
          setWishlisted(idsRes.data.includes(id));
          setEligibility(elig.data);
        } catch {}
      } else {
        setEligibility(null);
      }
    } catch {
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [id, isAuthed]);

  useEffect(() => { load(); }, [load]);

  const toggleWishlist = async () => {
    if (!isAuthed) { toast.info('Sign in to save events'); navigate('/login'); return; }
    if (wishBusy) return;
    setWishBusy(true);
    try {
      const r = await api.post(`/wishlist/${id}`);
      setWishlisted(r.data.wishlisted);
      toast.success(r.data.wishlisted ? 'Added to wishlist' : 'Removed from wishlist');
    } catch { toast.error('Could not update wishlist'); }
    finally { setWishBusy(false); }
  };

  const handleEnquire = () => {
    if (!isAuthed) { toast.info('Sign in to enquire'); navigate('/login'); return; }
    navigate(`/enquiry/${id}`);
  };

  const submitReview = async () => {
    if (!body.trim()) { toast.error('Please add a short review'); return; }
    setSubmittingReview(true);
    try {
      if (editingId) {
        await api.patch(`/reviews/${editingId}`, { rating, title: title.trim() || undefined, body: body.trim() });
        toast.success('Review updated');
      } else {
        await api.post(`/events/${id}/reviews`, { rating, title: title.trim() || undefined, body: body.trim() });
        toast.success('Review posted');
      }
      setReviewOpen(false);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSubmittingReview(false); }
  };

  const deleteReview = async () => {
    if (!window.confirm('Delete review?')) return;
    try {
      await api.delete(`/reviews/${eligibility.existing_review.id}`);
      toast.success('Review deleted');
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-magenta" /></div>;
  if (!event) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <AlertCircle className="w-12 h-12 text-gray-300" />
      <p className="text-gray-900 font-bold">Event not found</p>
      <button onClick={() => navigate(-1)} className="text-brand-magenta font-bold text-sm">Go back</button>
    </div>
  );

  const gallery = event.gallery?.length ? event.gallery : [event.banner_url];
  const galleryForView = isAuthed ? gallery : gallery.slice(0, 1);

  return (
    <div className="relative" data-testid="event-detail-page">
      {/* Banner */}
      <div className="relative">
        <div className="relative overflow-hidden">
          <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${carouselIdx * 100}%)` }}>
            {galleryForView.map((url, i) => (
              <img key={i} src={url} alt={`Gallery ${i}`} className="w-full h-[320px] object-cover flex-shrink-0" />
            ))}
          </div>
        </div>
        <div className="absolute top-0 left-0 right-0 flex justify-between items-start p-4 pt-6">
          <button data-testid="event-back-btn" onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white/95 shadow-md flex items-center justify-center hover:bg-white transition-colors">
            <ChevronLeft className="w-5 h-5 text-gray-900" />
          </button>
          <button data-testid="event-wishlist-btn" onClick={toggleWishlist} className="w-10 h-10 rounded-full bg-white/95 shadow-md flex items-center justify-center hover:bg-white transition-colors">
            <Heart className={`w-5 h-5 ${wishlisted ? 'text-red-500 fill-red-500' : 'text-gray-900'}`} />
          </button>
        </div>
        {galleryForView.length > 1 && (
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5">
            {galleryForView.map((_, i) => (
              <button key={i} onClick={() => setCarouselIdx(i)} className={`h-1.5 rounded-full transition-all ${i === carouselIdx ? 'w-5 bg-white' : 'w-1.5 bg-white/50'}`} />
            ))}
          </div>
        )}
      </div>

      <div className="max-w-4xl mx-auto -mt-5 relative bg-gray-50 rounded-t-3xl px-6 pt-6 pb-32">
        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          <span className="bg-pink-50 text-brand-magenta text-[11px] font-extrabold tracking-wide px-3 py-1 rounded-full">{event.category}</span>
          <span className="bg-gray-100 text-gray-600 text-[11px] font-extrabold px-3 py-1 rounded-full flex items-center gap-1">
            <Users className="w-3 h-3" /> Age {event.age_restriction}
          </span>
          {event.is_featured && (
            <span className="bg-amber-50 text-amber-600 text-[11px] font-extrabold px-3 py-1 rounded-full flex items-center gap-1">
              <Star className="w-3 h-3 fill-[#E91E63]" /> Featured
            </span>
          )}
        </div>

        <h1 className="text-2xl font-black text-gray-900 tracking-tight mt-3 leading-tight" data-testid="event-title">{event.title}</h1>

        {/* About — short desc always visible */}
        <h2 className="text-base font-black text-gray-900 mt-5 mb-2">About this event</h2>
        <p className={`text-sm text-gray-500 leading-relaxed ${!isAuthed ? 'line-clamp-3' : ''}`}>{event.description}</p>

        {/* Date always visible */}
        <div className="flex items-center gap-3 mt-4 p-3 bg-white rounded-xl border border-gray-100">
          <Calendar className="w-5 h-5 text-brand-magenta flex-shrink-0" />
          <div>
            <p className="text-sm font-extrabold text-gray-900">{dayjs(event.start_datetime).format('dddd, MMMM D, YYYY')}</p>
            <p className="text-xs text-gray-500">{dayjs(event.start_datetime).format('h:mm A')} — {dayjs(event.end_datetime).format('h:mm A')}</p>
          </div>
        </div>

        {isAuthed ? (
          <>
            {/* Organizer */}
            <div className="flex items-center gap-3 mt-4 p-3 bg-white rounded-xl border border-gray-100">
              <div className="w-9 h-9 rounded-full bg-brand-magenta flex items-center justify-center flex-shrink-0">
                <Megaphone className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="text-sm font-extrabold text-gray-900">{event.organizer_name}</span>
                  <CheckCircle className="w-3.5 h-3.5 brand-gradient-text" />
                </div>
                <span className="text-[11px] text-gray-500">Verified Organizer</span>
              </div>
            </div>

            {/* Venue */}
            <div className="mt-4 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <a href={`https://maps.google.com/?q=${event.venue_lat},${event.venue_lng}`} target="_blank" rel="noopener noreferrer" data-testid="event-map-btn" className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors">
                <MapPin className="w-5 h-5 text-brand-magenta flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-extrabold text-gray-900">{event.venue_name}</p>
                  <p className="text-xs text-gray-500 truncate">{event.venue_address}, {event.city}</p>
                </div>
                <span className="flex items-center gap-1 bg-pink-50 text-brand-magenta text-[11px] font-extrabold px-2.5 py-1.5 rounded-full">
                  <MapPin className="w-3 h-3" /> Map
                </span>
              </a>
            </div>

            {/* Stats */}
            <div className="flex mt-4 bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex-1 text-center">
                <p className="text-lg font-black text-gray-900 flex items-center justify-center gap-1"><Eye className="w-4 h-4 text-gray-400" />{event.view_count}</p>
                <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider">Views</p>
              </div>
              <div className="w-px bg-gray-100" />
              <div className="flex-1 text-center">
                <p className="text-lg font-black text-gray-900 flex items-center justify-center gap-1"><MessageSquare className="w-4 h-4 text-gray-400" />{event.enquiry_count}</p>
                <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider">Enquiries</p>
              </div>
              <div className="w-px bg-gray-100" />
              <div className="flex-1 text-center">
                <p className="text-lg font-black text-gray-900">{event.city}</p>
                <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider">City</p>
              </div>
            </div>

            {/* FAQs — auth-only */}
            {event.faqs && event.faqs.length > 0 && (
              <div className="mt-6 bg-white rounded-2xl border border-gray-100 p-4" data-testid="event-faq-section">
                <h2 className="text-base font-black text-gray-900 mb-1">Frequently Asked Questions</h2>
                {[...event.faqs].sort((a, b) => a.position - b.position).map((faq, idx) => (
                  <FAQAccordion key={faq.id} faq={faq} defaultOpen={idx === 0} />
                ))}
              </div>
            )}

            {/* Reviews */}
            <div className="mt-8" data-testid="event-reviews-section">
              <div className="flex justify-between items-start">
                <h2 className="text-base font-black text-gray-900">Reviews</h2>
                {reviews && reviews.total_reviews > 0 && (
                  <div className="text-right">
                    <p className="text-xl font-black text-gray-900">{reviews.avg_rating?.toFixed(1)}</p>
                    <Stars value={reviews.avg_rating || 0} />
                    <p className="text-[11px] text-gray-500 font-bold mt-0.5">{reviews.total_reviews} review{reviews.total_reviews === 1 ? '' : 's'}</p>
                  </div>
                )}
              </div>

              {reviews && reviews.total_reviews > 0 && (
                <div className="mt-3 bg-white rounded-2xl border border-gray-100 p-3 flex flex-col gap-1.5">
                  {[5, 4, 3, 2, 1].map((r) => {
                    const cnt = reviews.rating_distribution[String(r)] || 0;
                    const pct = reviews.total_reviews ? Math.round((cnt / reviews.total_reviews) * 100) : 0;
                    return (
                      <div key={r} className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-500 w-3">{r}</span>
                        <Star className="w-3 h-3 text-brand-magenta fill-[#E91E63]" />
                        <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full bg-pink-400 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[11px] text-gray-500 font-semibold w-6 text-right">{cnt}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {eligibility?.existing_review && (
                <div className="mt-3 bg-pink-50 rounded-2xl border border-pink-200 p-3" data-testid="my-review">
                  <div className="flex justify-between items-center">
                    <span className="text-[11px] font-black text-brand-magenta uppercase tracking-wider">Your review</span>
                    <div className="flex gap-2">
                      <button data-testid="edit-review-btn" onClick={() => { setEditingId(eligibility.existing_review.id); setRating(eligibility.existing_review.rating); setTitle(eligibility.existing_review.title || ''); setBody(eligibility.existing_review.body); setReviewOpen(true); }}><Edit className="w-4 h-4 text-brand-magenta" /></button>
                      <button data-testid="delete-review-btn" onClick={deleteReview}><Trash2 className="w-4 h-4 text-red-500" /></button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Stars value={eligibility.existing_review.rating} />
                    {eligibility.existing_review.title && <span className="text-sm font-extrabold text-gray-900">{eligibility.existing_review.title}</span>}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{eligibility.existing_review.body}</p>
                </div>
              )}

              {isAuthed && eligibility?.can_review && (
                <button data-testid="write-review-btn" onClick={() => { setEditingId(null); setRating(5); setTitle(''); setBody(''); setReviewOpen(true); }}
                  className="w-full mt-3 flex items-center justify-center gap-2 bg-brand-magenta hover:bg-pink-700 text-white py-3 rounded-2xl font-black text-sm transition-colors">
                  <Edit className="w-4 h-4" /> Write a Review
                </button>
              )}

              {isAuthed && !eligibility?.can_review && !eligibility?.existing_review && (
                <p data-testid="review-helper" className="text-center text-xs text-gray-400 py-3">Reviews open after the event is completed.</p>
              )}

              {reviews && reviews.reviews.length > 0 ? (
                <div className="mt-3 flex flex-col gap-2">
                  {reviews.reviews
                    .filter((r) => !eligibility?.existing_review || r.id !== eligibility.existing_review.id)
                    .slice(0, 5)
                    .map((r) => (
                      <div key={r.id} data-testid={`review-${r.id}`} className="bg-white rounded-2xl border border-gray-100 p-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-9 h-9 rounded-full bg-brand-magenta flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-sm font-black">{(r.user_name || 'A')[0]}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-extrabold text-gray-900">{r.user_name}</p>
                            <div className="flex items-center gap-1.5">
                              <Stars value={r.rating} size={12} />
                              <span className="text-[11px] text-gray-400">{'\u00b7'} {dayjs(r.created_at).format('MMM D')}</span>
                            </div>
                          </div>
                        </div>
                        {r.title && <p className="text-sm font-extrabold text-gray-900 mt-1.5">{r.title}</p>}
                        <p className="text-sm text-gray-500 mt-1 leading-relaxed">{r.body}</p>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No reviews yet.</p>
              )}
            </div>
          </>
        ) : (
          /* Guest gate */
          <button
            data-testid="event-guest-showmore-btn"
            onClick={() => setGateOpen(true)}
            className="w-full mt-6 flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-pink-50 border-[1.5px] border-brand-magenta text-brand-magenta font-extrabold text-sm hover:bg-pink-100 transition-colors"
          >
            <Lock className="w-4 h-4" />
            Show More Details
          </button>
        )}
      </div>

      {/* Guest Gate Modal */}
      {gateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
          <div className="absolute inset-0 bg-black/50" onClick={() => setGateOpen(false)} />
          <div className="relative bg-white rounded-3xl p-6 w-full max-w-sm text-center flex flex-col items-center gap-3" data-testid="event-guest-gate-sheet">
            <div className="w-14 h-14 rounded-full bg-pink-50 flex items-center justify-center mb-1">
              <Lock className="w-7 h-7 text-brand-magenta" />
            </div>
            <h3 className="text-lg font-black text-gray-900">Sign in to see full event details</h3>
            <p className="text-sm text-gray-500 leading-relaxed">View the venue, exact timing, organizer contact and gallery. Free — takes a minute.</p>
            <button data-testid="event-gate-signin-btn" onClick={() => { setGateOpen(false); navigate('/login'); }}
              className="w-full bg-brand-magenta hover:bg-pink-700 text-white py-3.5 rounded-2xl font-black text-[15px] transition-colors">
              Sign In
            </button>
            <button data-testid="event-gate-register-btn" onClick={() => { setGateOpen(false); navigate('/register'); }}
              className="w-full py-3.5 rounded-2xl font-bold text-sm border-[1.5px] border-gray-200 text-gray-900 hover:bg-gray-50 transition-colors">
              Create Account
            </button>
            <button data-testid="event-gate-close-btn" onClick={() => setGateOpen(false)} className="py-2 text-sm text-gray-400 font-bold hover:text-gray-600 transition-colors">Maybe later</button>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setReviewOpen(false)} />
          <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto p-6 flex flex-col gap-3">
            <div className="w-10 h-1 rounded-full bg-gray-300 mx-auto mb-2 sm:hidden" />
            <h3 className="text-xl font-black text-gray-900">{editingId ? 'Edit your review' : 'Write a review'}</h3>
            <label className="text-xs font-bold text-gray-500">Rating</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`review-star-${n}`} onClick={() => setRating(n)}>
                  <Star className={`w-8 h-8 ${n <= rating ? 'text-brand-magenta fill-[#E91E63]' : 'text-gray-300'}`} />
                </button>
              ))}
            </div>
            <label htmlFor="review-title" className="text-xs font-bold text-gray-500 mt-1">Title (optional)</label>
            <input id="review-title" data-testid="review-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Sum it up in a line" className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500" />
            <label htmlFor="review-body" className="text-xs font-bold text-gray-500 mt-1">Your review</label>
            <textarea id="review-body" data-testid="review-body-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Share your experience..." rows={4} className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 resize-none" />
            <button data-testid="review-submit-btn" onClick={submitReview} disabled={submittingReview}
              className="w-full bg-brand-magenta hover:bg-pink-700 disabled:opacity-70 text-white py-3.5 rounded-2xl font-black text-sm transition-colors mt-2">
              {submittingReview ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (editingId ? 'Save Changes' : 'Post Review')}
            </button>
          </div>
        </div>
      )}

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-xl border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] p-4 pb-safe md:pb-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <div className="flex-1">
            <p className="text-xs font-bold text-gray-500">Interested?</p>
            <p className="text-sm font-extrabold text-gray-900">Talk to the organizer directly</p>
          </div>
          <button data-testid="enquire-now-btn" onClick={handleEnquire}
            className="flex items-center gap-2 brand-gradient hover:opacity-90 text-white px-6 py-3.5 rounded-2xl font-black text-sm transition-all active:scale-[0.98]">
            <Phone className="w-4 h-4" />
            Enquire Now
          </button>
        </div>
      </div>
    </div>
  );
}
